/**
 * 前端请求签名工具
 *
 * 与后端 BPMService 的 AppSignatureValidator 保持一致：
 *   待签名串： AppId\nTenantId\nAccount\nTimestamp\nNonce
 *   签名算法： Base64( HMAC-SHA256(AppSecret, stringToSign) )
 *
 * 使用浏览器内置 Web Crypto API（SubtleCrypto），无需额外依赖。
 */

// 读取运行时配置（dist/config.js 可改），其次读取编译期环境变量，最后回退默认值。
const getAppId = () =>
  (window.__APP_CONFIG__ && window.__APP_CONFIG__.APP_ID) ||
  process.env.VUE_APP_APP_ID ||
  'G1WEB'

const getAppSecret = () =>
  (window.__APP_CONFIG__ && window.__APP_CONFIG__.APP_SECRET) ||
  process.env.VUE_APP_APP_SECRET ||
  'G1Web@HMAC#Secret$2026_ChangeMe!_5f3c9a'

// 生成随机 Nonce
const genNonce = () => {
  const arr = new Uint8Array(16)
  ;(window.crypto || window.msCrypto).getRandomValues(arr)
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('')
}

// 将 ArrayBuffer 转为 Base64
const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

// HMAC-SHA256 -> Base64
const hmacSha256Base64 = async (secret, message) => {
  const enc = new TextEncoder()
  const key = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await window.crypto.subtle.sign('HMAC', key, enc.encode(message))
  return bufferToBase64(signature)
}

/**
 * 为请求生成签名相关的请求头。
 * @param {string} tenantId 账套（X-Tenant-Id）
 * @param {string} account  账号（X-Account），未登录时可为空字符串
 * @returns {Promise<Object>} 需要附加到请求上的头部集合
 */
export const buildSignHeaders = async (tenantId = '', account = '') => {
  const appId = getAppId()
  const secret = getAppSecret()
  const timestamp = Date.now().toString()
  const nonce = genNonce()

  const stringToSign = [appId, tenantId || '', account || '', timestamp, nonce].join('\n')
  const signature = await hmacSha256Base64(secret, stringToSign)

  return {
    'X-App-Id': appId,
    'X-Tenant-Id': tenantId || '',
    'X-Account': account || '',
    'X-Timestamp': timestamp,
    'X-Nonce': nonce,
    'X-Signature': signature
  }
}
